import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { HoloLearningBadge } from '../components/HoloLearningBadge';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  TrendingUp, TrendingDown, Users, DollarSign, Target, FileText,
  Ticket, Sparkles, BarChart2, ArrowRight, Plus, UserPlus, X,
  CheckCircle2, Clock, AlertTriangle, Mail, Share2, Star,
  Activity, Briefcase, ShieldCheck, CalendarDays,
  ChevronRight, RefreshCw, Zap
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, LineChart, Line
} from 'recharts';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { TourStartButton } from '../components/SampleDataTour';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Scorecard ─────────────────────────────────────────────
const Scorecard = ({ label, value, icon: Icon, change, prefix = '', color, onClick, subtext }) => (
  <div
    className={cn("bg-white border border-zinc-200 rounded-xl p-5 flex flex-col justify-between transition-all hover:shadow-md", onClick && "cursor-pointer")}
    onClick={onClick}
    data-testid={`sc-${label.toLowerCase().replace(/[\s&]+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", color || "bg-emerald-50 text-emerald-600")}>
        <Icon className="h-5 w-5" />
      </div>
      {change !== undefined && change !== null && (
        <span className={cn("inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full",
          change >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600")}>
          {change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {Math.abs(change)}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold tracking-tight text-zinc-900">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</p>
    <p className="text-xs font-medium text-zinc-500 mt-1">{label}</p>
    {subtext && <p className="text-[10px] text-zinc-400 mt-0.5">{subtext}</p>}
  </div>
);

// ─── Mini Stat Row ─────────────────────────────────────────
const MiniStat = ({ label, value, icon: Icon, color }) => (
  <div className="flex items-center gap-3 py-2" data-testid={`mini-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", color || "bg-zinc-100 text-zinc-500")}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] text-zinc-500">{label}</p>
      <p className="text-sm font-semibold text-zinc-900">{value}</p>
    </div>
  </div>
);

// ─── Chart Tooltip ─────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-zinc-900 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-[11px]" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
export default function SubscriberDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { format: formatCurrency } = useCurrency();
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sc, rev, funnel, hr, svc, mkt, goals, tkt] = await Promise.all([
        axios.get(`${API}/api/dashboard/bi/scorecards?period=${period}`),
        axios.get(`${API}/api/dashboard/bi/revenue-trend`),
        axios.get(`${API}/api/dashboard/bi/pipeline-funnel`),
        axios.get(`${API}/api/dashboard/bi/hr-pulse?period=${period}`),
        axios.get(`${API}/api/dashboard/bi/service-health?period=${period}`),
        axios.get(`${API}/api/dashboard/bi/marketing?period=${period}`),
        axios.get(`${API}/api/dashboard/bi/goals`),
        axios.get(`${API}/api/dashboard/bi/tickets-trend`),
      ]);
      setData({
        sc: sc.data, rev: rev.data.months, funnel: funnel.data,
        hr: hr.data, svc: svc.data, mkt: mkt.data,
        goals: goals.data, tkt: tkt.data.weeks,
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sc = data.sc || {};
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  })();
  const firstName = (user?.full_name || 'Boss').split(' ')[0];
  const periodLabel = { today: 'Today', week: 'This Week', month: 'This Month', year: 'This Year' }[period] || period;

  const [setupDismissed, setSetupDismissed] = useState(() => sessionStorage.getItem('setup_banner_dismissed') === 'true');
  const [hasDemoData, setHasDemoData] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/spq/demo-data-status`).then(r => {
      if (r.data.active) setHasDemoData(true);
    }).catch(() => {});
  }, []);

  const dismissSetup = () => { setSetupDismissed(true); sessionStorage.setItem('setup_banner_dismissed', 'true'); };

  return (
    <div className="space-y-6" data-testid="subscriber-dashboard">
      {/* ─── Setup Prompt (shows when demo data active) ─── */}
      {hasDemoData && !setupDismissed && (
        <div className="relative bg-gradient-to-r from-violet-50 via-indigo-50 to-blue-50 border border-violet-200 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="setup-prompt-banner">
          <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-violet-900">Your business is pre-configured with sample data</p>
            <p className="text-xs text-violet-600 mt-0.5">Edit each section with your real company details to start running your business on Konnect.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <TourStartButton className="bg-violet-600 hover:bg-violet-700 text-white" />
            <Button size="sm" variant="outline" className="text-xs gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-100" onClick={() => navigate('/overview/onboarding')} data-testid="setup-prompt-btn">
              <ArrowRight className="h-3.5 w-3.5" />Edit with Your Details
            </Button>
            <button onClick={dismissSetup} className="text-violet-300 hover:text-violet-500 p-1" data-testid="dismiss-setup-prompt">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900" data-testid="dashboard-greeting">
              {greeting}, {firstName}
            </h1>
            <HoloLearningBadge />
          </div>
          <p className="text-sm text-zinc-500 mt-0.5">Here&apos;s your business overview for <span className="font-medium text-zinc-700">{periodLabel}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="bg-zinc-100 h-9">
              <TabsTrigger value="today" className="text-xs px-3" data-testid="tab-today">Today</TabsTrigger>
              <TabsTrigger value="week" className="text-xs px-3" data-testid="tab-week">Week</TabsTrigger>
              <TabsTrigger value="month" className="text-xs px-3" data-testid="tab-month">Month</TabsTrigger>
              <TabsTrigger value="year" className="text-xs px-3" data-testid="tab-year">Year</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="ghost" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-dashboard">
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* ─── Revenue Scorecards ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="scorecards-row">
        <Scorecard
          label="Revenue Collected" value={formatCurrency(sc.revenue_collected?.value || 0)}
          icon={DollarSign} change={sc.revenue_collected?.change} color="bg-emerald-50 text-emerald-600"
          subtext={`vs ${formatCurrency(sc.revenue_collected?.prev || 0)} prev`}
          onClick={() => navigate('/sales/invoices')}
        />
        <Scorecard
          label="Pipeline Value" value={formatCurrency(sc.pipeline_value?.value || 0)}
          icon={Target} color="bg-blue-50 text-blue-600"
          onClick={() => navigate('/leads/pipeline')}
        />
        <Scorecard
          label="New Leads" value={sc.leads_new?.value || 0}
          icon={UserPlus} change={sc.leads_new?.change} color="bg-violet-50 text-violet-600"
          subtext={`${sc.leads_total?.value || 0} total`}
          onClick={() => navigate('/leads/pipeline')}
        />
        <Scorecard
          label="Proposals Won" value={sc.proposals_won?.value || 0}
          icon={FileText} change={sc.proposals_won?.change} color="bg-amber-50 text-amber-600"
          subtext={`${sc.proposals_pending?.value || 0} pending`}
          onClick={() => navigate('/sales/proposals')}
        />
      </div>

      {/* ─── Revenue Trend + Goals ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Revenue Chart */}
        <Card className="lg:col-span-8 bg-white border-zinc-200" data-testid="revenue-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <BarChart2 className="h-4 w-4 text-emerald-500" />Revenue Trend (6 Months)
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-zinc-500 gap-1 h-7" onClick={() => navigate('/analytics')}>
                Details <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.rev || []} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} fill="url(#revGrad)" name="Revenue" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Goals */}
        <Card className="lg:col-span-4 bg-white border-zinc-200" data-testid="goals-tracker">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <Target className="h-4 w-4 text-blue-500" />Goals
              </CardTitle>
              <Badge className="bg-zinc-100 text-zinc-600 text-[10px]">{data.goals?.completed || 0} done / {data.goals?.active || 0} active</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.goals?.goals || []).length === 0 && (
              <div className="text-center py-6"><Target className="h-6 w-6 text-zinc-300 mx-auto mb-2" /><p className="text-xs text-zinc-400">No active goals</p></div>
            )}
            {(data.goals?.goals || []).slice(0, 5).map((g, i) => (
              <div key={i} className="space-y-1" data-testid={`goal-${i}`}>
                <div className="flex justify-between items-center">
                  <p className="text-xs font-medium text-zinc-700 truncate max-w-[70%]">{g.title}</p>
                  <span className="text-[10px] font-semibold text-zinc-500">{g.progress || 0}%</span>
                </div>
                <Progress value={g.progress || 0} className="h-1.5" />
                <p className="text-[10px] text-zinc-400">{g.current_value || 0}/{g.target_value || 0} {g.unit || ''}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* ─── Pipeline Funnel + Service Health ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Funnel */}
        <Card className="lg:col-span-4 bg-white border-zinc-200" data-testid="pipeline-funnel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-violet-500" />Sales Funnel
              </CardTitle>
              <Badge className="bg-emerald-50 text-emerald-600 text-[10px]">{data.funnel?.conversion_rate || 0}% conv.</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data.funnel?.funnel || []).map((s, i) => {
                const maxCount = Math.max(...(data.funnel?.funnel || []).map(f => f.count), 1);
                const pct = Math.max((s.count / maxCount) * 100, 4);
                const colors = ['bg-blue-500', 'bg-sky-500', 'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500', 'bg-green-600', 'bg-zinc-300'];
                return (
                  <div key={i} data-testid={`funnel-${s.stage}`}>
                    <div className="flex justify-between text-[11px] mb-0.5">
                      <span className="text-zinc-600 font-medium">{s.stage}</span>
                      <span className="text-zinc-500">{s.count}</span>
                    </div>
                    <div className="w-full bg-zinc-100 rounded-full h-2">
                      <div className={cn("h-2 rounded-full transition-all", colors[i % colors.length])} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Tickets Trend */}
        <Card className="lg:col-span-4 bg-white border-zinc-200" data-testid="tickets-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
              <Ticket className="h-4 w-4 text-orange-500" />Tickets: Opened vs Resolved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.tkt || []} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="opened" fill="#f97316" radius={[3, 3, 0, 0]} barSize={10} name="Opened" />
                  <Bar dataKey="resolved" fill="#10B981" radius={[3, 3, 0, 0]} barSize={10} name="Resolved" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Service Health Summary */}
        <Card className="lg:col-span-4 bg-white border-zinc-200" data-testid="service-health">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-teal-500" />Service Health
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <MiniStat label="Open Tickets" value={data.svc?.open_tickets || 0} icon={AlertTriangle} color="bg-orange-50 text-orange-500" />
            <MiniStat label={`Resolved (${periodLabel})`} value={data.svc?.resolved_period?.value || 0} icon={CheckCircle2} color="bg-emerald-50 text-emerald-500" />
            <MiniStat label="SLA Compliance" value={`${data.svc?.sla_compliance || 100}%`} icon={ShieldCheck} color="bg-teal-50 text-teal-500" />
            <MiniStat label="Total All-Time" value={data.svc?.total_tickets || 0} icon={Ticket} color="bg-zinc-100 text-zinc-500" />
          </CardContent>
        </Card>
      </div>

      {/* ─── HR Pulse + Marketing + Quick Actions ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* HR */}
        <Card className="lg:col-span-4 bg-white border-zinc-200" data-testid="hr-pulse">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-500" />Team Pulse
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" onClick={() => navigate('/operations/attendance')}>
                View <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            <MiniStat label="Team Size" value={data.hr?.staff_count || 0} icon={Users} color="bg-indigo-50 text-indigo-500" />
            <MiniStat label="Attendance Rate" value={`${data.hr?.attendance_rate || 0}%`} icon={CalendarDays} color="bg-emerald-50 text-emerald-500" />
            <MiniStat label="Pending Evaluations" value={data.hr?.pending_evaluations || 0} icon={Clock} color="bg-amber-50 text-amber-500" />
            <MiniStat label="Open Escalations" value={data.hr?.open_escalations || 0} icon={AlertTriangle}
              color={data.hr?.open_escalations > 0 ? "bg-rose-50 text-rose-500" : "bg-zinc-100 text-zinc-400"} />
          </CardContent>
        </Card>

        {/* Marketing */}
        <Card className="lg:col-span-4 bg-white border-zinc-200" data-testid="marketing-perf">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />Marketing
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" onClick={() => navigate('/content')}>
                Studio <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            <MiniStat label="Email Campaigns" value={data.mkt?.campaigns?.value || 0} icon={Mail} color="bg-purple-50 text-purple-500" />
            <MiniStat label="Social Posts" value={data.mkt?.social_posts?.value || 0} icon={Share2} color="bg-sky-50 text-sky-500" />
            <MiniStat label="AI Content Generated" value={data.mkt?.content_generated?.value || 0} icon={Sparkles} color="bg-violet-50 text-violet-500" />
            <MiniStat label="Avg Review Rating" value={data.mkt?.avg_review ? `${data.mkt.avg_review}/5` : 'N/A'} icon={Star} color="bg-amber-50 text-amber-500" />
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="lg:col-span-4 bg-white border-zinc-200" data-testid="quick-actions">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" />Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            {[
              { label: 'New Invoice', icon: DollarSign, path: '/sales/invoices', color: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' },
              { label: 'Add Lead', icon: UserPlus, path: '/leads/pipeline', color: 'bg-blue-50 text-blue-600 hover:bg-blue-100' },
              { label: 'New Ticket', icon: Ticket, path: '/service/helpdesk', color: 'bg-orange-50 text-orange-600 hover:bg-orange-100' },
              { label: 'Add Staff', icon: Users, path: '/hr/staff', color: 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100' },
              { label: 'New Proposal', icon: FileText, path: '/sales/proposals', color: 'bg-amber-50 text-amber-600 hover:bg-amber-100' },
              { label: 'AI Content', icon: Sparkles, path: '/content', color: 'bg-purple-50 text-purple-600 hover:bg-purple-100' },
            ].map(a => (
              <button
                key={a.label}
                onClick={() => navigate(a.path)}
                className={cn("flex items-center gap-2 p-3 rounded-lg text-xs font-medium transition-colors", a.color)}
                data-testid={`qa-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <a.icon className="h-4 w-4" />
                {a.label}
              </button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* ─── Secondary Scorecards ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="secondary-scorecards">
        <Scorecard
          label="Clients" value={sc.clients_total?.value || 0}
          icon={Briefcase} change={sc.clients_new?.change} color="bg-sky-50 text-sky-600"
          subtext={`${sc.clients_new?.value || 0} new ${periodLabel.toLowerCase()}`}
          onClick={() => navigate('/sales/clients')}
        />
        <Scorecard
          label="Pending Invoices" value={formatCurrency(sc.revenue_pending?.value || 0)}
          icon={Clock} color="bg-amber-50 text-amber-600"
          onClick={() => navigate('/sales/invoices')}
        />
        <Scorecard
          label="Overdue" value={formatCurrency(sc.revenue_overdue?.value || 0)}
          icon={AlertTriangle} color="bg-rose-50 text-rose-600"
          onClick={() => navigate('/sales/invoices')}
        />
        <Scorecard
          label="Total Leads" value={sc.leads_total?.value || 0}
          icon={Activity} color="bg-zinc-100 text-zinc-600"
          onClick={() => navigate('/leads/pipeline')}
        />
      </div>
    </div>
  );
}
